import { Form, notification } from "antd"
import React, { useCallback, useEffect } from "react"
import { useRecoilState } from "recoil"

import Btn from "components/atoms/Btn"
import TextField from "components/atoms/TextField"
import logger from "lib/logger"

import { editProfile } from "../../actions/profile"
import { t } from "../../lib/i18n"
import useProfile from "../../recoils/atoms/profile"
import useProfileNameDraft from "../../recoils/atoms/profileNameDraft"
import useAuth from "../../recoils/atoms/useAuth"

type ProfileFormSchema = {
  display_name: string
  email: string
}

function ProfileEditInput(): JSX.Element {
  const [profile, setProfile] = useRecoilState(useProfile)
  const [auth, setAuth] = useRecoilState(useAuth)
  const [nameDraft, setNameDraft] = useRecoilState(useProfileNameDraft)
  const [form] = Form.useForm<ProfileFormSchema>()

  const onSubmit = useCallback(async () => {
    const currentFormValue = form.getFieldsValue()
    const profileRequestPayload = {
      ...profile,
      name: currentFormValue.display_name,
    }

    try {
      await editProfile(profileRequestPayload)
      setAuth({ ...auth, name: profileRequestPayload.name })
      setProfile({ ...profileRequestPayload })
      // 保存できたので下書きは役目を終える。残すと、次に profile を読み直しても
      // 下書きが勝ち続けて「保存済みの値が出てこない」形になる
      setNameDraft(null)
      notification.success({
        message: t("saved_profile"),
      })
    } catch (error) {
      logger("Failed to edit profile", error)
      notification.error({
        message: t("unknown_error"),
      })
    }
  }, [profile, auth, setProfile, setAuth, setNameDraft])

  // 入力を Form インスタンスの外（recoil）へ逃がす。チーム変更で
  // このコンポーネントごと作り直されても消えないようにするため（#432）
  const onValuesChange = (changed: Partial<ProfileFormSchema>) => {
    if (changed.display_name === undefined) return

    setNameDraft(changed.display_name)
  }

  const onFailed = () => {
    notification.error({
      message: t("input_error"),
    })
  }

  // 下書きがあればそれを優先する。**読み込み直後の値で入力を上書きしないこと**
  // が #432 の要点で、この effect は profile の読み込み完了でも走るため。
  //
  // 入れ替える前に今の値と比べているのは、`nameDraft` を deps に入れている
  // からです。1 文字打つたびに effect が走るので、無条件に `setFieldsValue`
  // すると **IME の変換中に値を入れ直してしまいます**（表示名は日本語で
  // 打たれる）。同じ値なら何もしないので、変換中は触りません。
  useEffect(() => {
    const nextDisplayName = nameDraft ?? profile.name

    if (form.getFieldValue("display_name") !== nextDisplayName) {
      form.setFieldsValue({ display_name: nextDisplayName })
    }

    if (form.getFieldValue("email") !== auth.email) {
      form.setFieldsValue({ email: auth.email })
    }
  }, [form, profile, auth, nameDraft])

  return (
    <Form
      form={form}
      name="profile"
      onFinish={onSubmit}
      onFinishFailed={onFailed}
      onValuesChange={onValuesChange}
    >
      <div style={styles.inputLayer}>
        <TextField
          label={t("display_name")}
          name="display_name"
          placeholder={t("enter_your_display_name")}
          labelStyle={styles.label}
          style={styles.input}
          validateFirst={true}
          rules={[{ required: true, message: t("display_name_required") }]}
          isLarge
        />
      </div>

      <div style={styles.inputLayer}>
        <TextField
          label={t("email")}
          name="email"
          labelStyle={styles.label}
          style={styles.input}
          validateFirst={true}
          rules={[{ required: true }]}
          disabled={true}
          type="email"
          isLarge
        />
      </div>

      <div style={{ display: "flex" }}>
        <Btn style={styles.save} htmlType="submit">
          {t("update_profile")}
        </Btn>
      </div>
    </Form>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  flex: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 48,
  },
  inputLayer: {
    width: "100%",
    marginBottom: "30px",
  },
  input: {
    height: 48,
  },
  label: {
    display: "inline-block",
    textTransform: "uppercase",
    marginBottom: 12,
  },
  save: {
    background: "#6f6eff",
    borderRadius: 4,
    border: "none",
    fontSize: 16,
    lineHeight: "16px",
    width: 159,
    height: 48,
  },
}

export default ProfileEditInput
